import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

import { API_BASE as API_URL } from '../lib/apiBase';
const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('hrms-token'));
  const [loading, setLoading] = useState(false);
  const refreshTimerRef = useRef(null);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
        credentials: 'include'
      });
      
      if (!response.ok) {
        let message = 'Login failed. Please try again.';
        try {
          const error = await response.json();
          message = error.detail || message;
        } catch {
          if (response.status === 401) message = 'Email or password is incorrect. Please check and try again.';
          else if (response.status === 403) message = 'Your account is not active. Please contact HR.';
          else if (response.status >= 500) message = 'Server is busy. Please wait a moment and try again.';
        }
        throw new Error(message);
      }
      
      const data = await response.json();
      setToken(data.access_token);
      setUser(data.user);
      localStorage.setItem('hrms-token', data.access_token);
      return data;
    } catch (e) {
      if (e.name === 'TypeError' || e.message === 'Failed to fetch') {
        throw new Error('Unable to connect to server. Please check your internet connection and try again.');
      }
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  const register = useCallback(async (userData) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData),
        credentials: 'include'
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Registration failed');
      }
      
      const data = await response.json();
      setToken(data.access_token);
      setUser(data.user);
      localStorage.setItem('hrms-token', data.access_token);
      return data;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (e) {
      console.error('Logout error:', e);
    }
    setToken(null);
    setUser(null);
    localStorage.removeItem('hrms-token');
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        return userData;
      }
      return null;
    } catch (e) {
      console.error('Auth check error:', e);
      return null;
    }
  }, [token]);

  // Refresh user (permissions, profile, role) from server
  const refreshUser = checkAuth;

  // Auto-refresh user state on mount, on window focus, and every 60s
  // This ensures permission/role/profile changes by admin propagate to live sessions.
  useEffect(() => {
    if (!token) return;
    refreshUser();
    const onFocus = () => refreshUser();
    window.addEventListener('focus', onFocus);
    if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    refreshTimerRef.current = setInterval(refreshUser, 60000);
    return () => {
      window.removeEventListener('focus', onFocus);
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, [token, refreshUser]);

  const value = {
    user,
    setUser,
    token,
    setToken,
    loading,
    login,
    register,
    logout,
    checkAuth,
    refreshUser,
    isAuthenticated: !!user || !!token
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
